import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Plus, Trash2, AlertTriangle, ArrowLeft } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SearchableSelect, { type SearchableSelectOption } from '../components/SearchableSelect';
import AsyncItemSelect from '../components/AsyncItemSelect';
import ItemFormModal from '../components/ItemFormModal';
import CustomerFormModal from '../components/CustomerFormModal';
import AttachmentsSection from '../components/AttachmentsSection';
import {
  createInvoice,
  updateInvoice,
  getInvoiceById,
  postInvoice,
  getCustomerReceivableInvoices,
} from '../utils/invoiceApi';
import { getCustomerPayments } from '../utils/customerPaymentApi';
import { getInvoiceAttachments } from '../utils/itemAttachmentApi';
import {
  listStagedAttachments,
  processAttachmentChanges,
  syncStagedAttachmentNotes,
} from '../utils/attachmentApi';
import { getCustomers, getCustomerById } from '../utils/customerApi';
import { ApiValidationError } from '../types/auth';
import {
  attachmentToEditable,
  buildIncludeOnSendById,
  getStagedAttachmentIds,
  hasPendingUploadFailed,
  hasPendingUploadInProgress,
  stagedAttachmentToPending,
} from '../types/attachmentsForm';
import type { EditableAttachment, PendingAttachment } from '../types/attachmentsForm';
import {
  INVOICE_UPLOAD_SESSION_KEY,
  getOrCreateUploadSessionId,
  resetUploadSessionId,
} from '../utils/uploadSession';
import DecimalInput from '../components/DecimalInput';
import { btnPrimary, selectInputOnFocus } from '../utils/uiTheme';
import { blurActiveInputBeforeSave } from '../utils/numberInput';
import { formatCurrency, formatPreviewQuantity } from '../utils/formatCurrency';
import { canEditInvoice } from '../utils/invoiceEditability';
import { toast, toastApiError, toastRouteSuccess } from '../utils/toast';
import type { CreateInvoiceData, CustomerSnapshot, Invoice, InvoiceLine, UpdateInvoiceData } from '../types/invoice';
import type { Customer, PaymentTerms } from '../types/customer';
import type { Item } from '../types/item';
import { addDaysToIsoDate, todayLocalIso } from '../utils/localDate';
import { scrollIntoViewAfterPaint } from '../utils/scrollIntoViewAfterPaint';
import { getCompany } from '../utils/companyApi';
import {
  getAllowNegativeInventory,
  isAdminRole,
  lineExceedsOnHand,
  parseInventoryQty,
  qtyOnHandClassName,
} from '../utils/negativeInventory';
import {
  type LinePriceSource,
  formatLineAmountDisplay,
  lineGross,
  round2,
  round7,
  salesApplyAmountDraftsToLines,
  salesCommitAmountEntry,
  salesDisplayRate,
  salesPayloadUnitPrice,
  salesQtyChangePricingPatch,
  salesRateChangePricingPatch,
} from '../utils/linePricing';
import {
  customerToSnapshot,
  emptyCustomerSnapshot,
  snapshotToPayload,
  validateCustomerSnapshot,
} from '../utils/customerSnapshot';

const PAYMENT_TERMS: PaymentTerms[] = ['IMMEDIATE', 'NET_30', 'NET_60', 'NET_90'];

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const customerToOption = (customer: Customer): SearchableSelectOption => ({
  value: customer.id,
  label: customer.display_name,
  description: [customer.email, customer.phone].filter(Boolean).join(' · ') || undefined,
});

const dueDateFromTerms = (txnDate: string, terms: PaymentTerms) => {
  switch (terms) {
    case 'NET_30':
      return addDaysToIsoDate(txnDate, 30);
    case 'NET_60':
      return addDaysToIsoDate(txnDate, 60);
    case 'NET_90':
      return addDaysToIsoDate(txnDate, 90);
    default:
      return txnDate;
  }
};

const canManageInvoices = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

/** Estimated VAT % for preview — server applies company tax codes on save */
const ESTIMATED_TAX_RATE = 5;

type DiscountType = 'percent' | 'amount';

interface LocalLine {
  item_id: number;
  item_name: string;
  item_sku: string;
  item_type: Item['item_type'];
  qty_on_hand: string;
  dealer_selling_price: string;
  cash_price: string;
  taxable: boolean;
  qty: number;
  unit_price: number;
  line_amount: number | null;
  discount_type: DiscountType;
  discount_percent: number;
  discount_amount: number;
  description: string;
  price_source: LinePriceSource;
}

const estimateLineTotals = (line: LocalLine) => {
  const gross = lineGross(line);
  const discountAmount =
    line.discount_type === 'amount'
      ? Math.min(line.discount_amount, gross)
      : gross * (line.discount_percent / 100);
  const net = gross - discountAmount;
  const taxAmount = line.taxable ? net * (ESTIMATED_TAX_RATE / 100) : 0;
  return {
    discountAmount: round2(discountAmount),
    net: round2(net),
    taxAmount: round2(taxAmount),
    lineTotal: round2(net + taxAmount),
  };
};

const estimateInvoiceTotals = (lines: LocalLine[]) => {
  let discountTotal = 0;
  let subtotal = 0;
  let taxTotal = 0;

  for (const line of lines) {
    const { discountAmount, net, taxAmount } = estimateLineTotals(line);
    discountTotal += discountAmount;
    subtotal += net;
    taxTotal += taxAmount;
  }

  discountTotal = round2(discountTotal);
  subtotal = round2(subtotal);
  taxTotal = round2(taxTotal);

  return {
    discountTotal,
    subtotal,
    taxTotal,
    total: round2(subtotal + taxTotal),
  };
};

/** Split an invoice-level amount discount across lines by gross, then top up leftover capacity. */
const allocateAmountDiscountByGross = (active: LocalLine[], value: number): number[] => {
  const n = active.length;
  if (n === 0) return [];

  const grosses = active.map(lineGross);
  const invoiceGross = round2(grosses.reduce((sum, g) => sum + g, 0));
  if (invoiceGross <= 0) return grosses.map(() => 0);

  const discount = Math.min(Math.max(0, round2(value)), invoiceGross);
  const amounts = new Array<number>(n).fill(0);

  let allocated = 0;
  for (let i = 0; i < n - 1; i++) {
    const share = round2((discount * grosses[i]) / invoiceGross);
    amounts[i] = Math.min(Math.max(0, share), grosses[i]);
    allocated = round2(allocated + amounts[i]);
  }
  amounts[n - 1] = Math.min(Math.max(0, round2(discount - allocated)), grosses[n - 1]);

  let leftover = round2(discount - amounts.reduce((sum, a) => round2(sum + a), 0));
  for (let i = 0; leftover > 0 && i < n; i++) {
    const room = round2(grosses[i] - amounts[i]);
    if (room <= 0) continue;
    const add = Math.min(room, leftover);
    amounts[i] = round2(amounts[i] + add);
    leftover = round2(leftover - add);
  }

  return amounts;
};

interface InvoiceFormData {
  customer_id: number;
  customer_snapshot: CustomerSnapshot;
  txn_date: string;
  due_date: string;
  payment_terms: PaymentTerms;
  memo: string;
  notes: string;
  allow_negative_stock: boolean;
  lines: LocalLine[];
}


const INITIAL_EMPTY_LINE_COUNT = 4;

const createEmptyLine = (): LocalLine => ({
  item_id: 0,
  item_name: '',
  item_sku: '',
  item_type: 'INVENTORY',
  qty_on_hand: '0',
  dealer_selling_price: '0',
  cash_price: '0',
  taxable: true,
  qty: 1,
  unit_price: 0,
  line_amount: null,
  discount_type: 'percent',
  discount_percent: 0,
  discount_amount: 0,
  description: '',
  price_source: 'rate',
});

const createInitialLines = (count = INITIAL_EMPTY_LINE_COUNT) =>
  Array.from({ length: count }, () => createEmptyLine());

/** Keep one blank row at the end so the next item can be entered (QuickBooks-style). */
const withTrailingEmptyLine = (lines: LocalLine[]): LocalLine[] => {
  if (lines.length === 0) return [createEmptyLine()];
  if (lines[lines.length - 1].item_id > 0) {
    return [...lines, createEmptyLine()];
  }
  return lines;
};

const mapInvoiceLineToLocal = (line: InvoiceLine): LocalLine => {
  const discount_percent = Number.parseFloat(line.discount_percent) || 0;
  const discount_amount = Number.parseFloat(line.discount_amount) || 0;
  const discount_type: DiscountType =
    discount_amount > 0 && discount_percent === 0 ? 'amount' : 'percent';

  return {
    item_id: line.item_id,
    item_name: line.item_snapshot.name,
    item_sku: line.item_snapshot.sku,
    item_type: line.item?.item_type ?? 'INVENTORY',
    qty_on_hand: line.item?.qty_on_hand ?? '0',
    dealer_selling_price: line.item?.dealer_selling_price ?? '0',
    cash_price: line.item?.cash_price ?? '0',
    taxable: line.item?.taxable ?? true,
    qty: parseFloat(line.qty),
    unit_price: round7(parseFloat(line.unit_price)),
    line_amount: null,
    discount_type,
    discount_percent,
    discount_amount,
    description: line.description ?? '',
    price_source: 'rate',
  };
};

const lineInputClass =
  'w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500';

const autoResize = (el: HTMLTextAreaElement) => {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

export type SalesManageSaveReason = 'draft_created' | 'draft_updated' | 'posted' | 'amended';

export type SalesManageProps = {
  embedded?: boolean;
  invoiceId?: number;
  onClose?: () => void;
  onInvoiceSaved?: (invoice: Invoice, reason: SalesManageSaveReason) => void;
};

const SalesManage = ({
  embedded = false,
  invoiceId: invoiceIdProp,
  onClose,
  onInvoiceSaved,
}: SalesManageProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryEditId = Number(searchParams.get('id')) || 0;
  const resolvedEditId =
    invoiceIdProp != null && invoiceIdProp > 0 ? invoiceIdProp : queryEditId;
  const isEditMode = resolvedEditId > 0;

  const [customerOptions, setCustomerOptions] = useState<SearchableSelectOption[]>([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState<SearchableSelectOption | null>(
    null
  );
  const selectedCustomerOptionRef = useRef<SearchableSelectOption | null>(null);
  const customerLookupRef = useRef<Map<number, Customer>>(new Map());
  const skipNextLoadRef = useRef(false);
  const lineRowRefs = useRef(new Map<number, HTMLTableRowElement>());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [createItemLineIndex, setCreateItemLineIndex] = useState<number | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<EditableAttachment[]>([]);
  const [uploadSessionId, setUploadSessionId] = useState(() =>
    getOrCreateUploadSessionId(INVOICE_UPLOAD_SESSION_KEY)
  );
  const [savePhase, setSavePhase] = useState<'idle' | 'saving' | 'attachments' | 'posting'>('idle');
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [lineAmountDrafts, setLineAmountDrafts] = useState<Record<number, string>>({});
  const [invoiceDiscountDraft, setInvoiceDiscountDraft] = useState<string | null>(null);
  const [invoiceDiscountType, setInvoiceDiscountType] = useState<DiscountType>('percent');
  const [allowNegativeInventory, setAllowNegativeInventory] = useState(() =>
    getAllowNegativeInventory()
  );
  const [loadingCustomerAccount, setLoadingCustomerAccount] = useState(false);
  const [customerOpenBalance, setCustomerOpenBalance] = useState(0);
  const [customerUnappliedTotal, setCustomerUnappliedTotal] = useState(0);

  const showNegativeStockOption = allowNegativeInventory && isAdminRole();

  const submitting = savePhase !== 'idle';
  const isDraftEdit = editingInvoice?.status === 'DRAFT';
  const isOpenAmend = editingInvoice?.status === 'OPEN';

  const today = todayLocalIso();
  const [formData, setFormData] = useState<InvoiceFormData>({
    customer_id: 0,
    customer_snapshot: emptyCustomerSnapshot(),
    txn_date: today,
    due_date: today,
    payment_terms: 'IMMEDIATE',
    memo: '',
    notes: '',
    allow_negative_stock: false,
    lines: [],
  });

  useEffect(() => {
    if (!canManageInvoices()) {
      if (embedded) {
        onClose?.();
      } else {
        navigate('/sales');
      }
      return;
    }
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedEditId, embedded]);

  useEffect(() => {
    if (!embedded && toastRouteSuccess(location.state)) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [embedded, location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    selectedCustomerOptionRef.current = selectedCustomerOption;
  }, [selectedCustomerOption]);

  const loadCustomerAccountSummary = useCallback(async (customerId: number, excludeInvoiceId = 0) => {
    setLoadingCustomerAccount(true);
    try {
      const [receivableInvoices, unappliedPaymentsList] = await Promise.all([
        getCustomerReceivableInvoices(customerId),
        getCustomerPayments({ customer_id: customerId, unapplied: true }),
      ]);
      const openBalance = receivableInvoices
        .filter((invoice) => invoice.id !== excludeInvoiceId)
        .reduce((sum, invoice) => sum + parseFloat(invoice.balance), 0);
      const unapplied = unappliedPaymentsList
        .filter((payment) => (payment.status ?? 'POSTED') !== 'VOIDED')
        .reduce((sum, payment) => sum + parseFloat(payment.unapplied_amount), 0);
      setCustomerOpenBalance(openBalance);
      setCustomerUnappliedTotal(unapplied);
    } catch {
      setCustomerOpenBalance(0);
      setCustomerUnappliedTotal(0);
    } finally {
      setLoadingCustomerAccount(false);
    }
  }, []);

  useEffect(() => {
    if (formData.customer_id > 0) {
      void loadCustomerAccountSummary(formData.customer_id, resolvedEditId);
    } else {
      setCustomerOpenBalance(0);
      setCustomerUnappliedTotal(0);
      setLoadingCustomerAccount(false);
    }
  }, [formData.customer_id, loadCustomerAccountSummary, resolvedEditId]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setCustomerSearchLoading(true);
      try {
        const result = await getCustomers({
          search: customerSearchInput.trim() || undefined,
          per_page: 20,
          page: 1,
        });
        for (const customer of result.customers) {
          customerLookupRef.current.set(customer.id, customer);
        }
        const options = result.customers.map(customerToOption);
        const selected = selectedCustomerOptionRef.current;
        setCustomerOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  const goToEditInvoice = useCallback(
    (id: number) => {
      if (embedded) {
        navigate(
          { pathname: `/sales-transactions/invoice/${id}`, search: location.search },
          { replace: true, preventScrollReset: true }
        );
      } else {
        navigate(`/manage-sales?id=${id}`, { replace: true });
      }
    },
    [embedded, location.search, navigate]
  );

  const finishPostedOrAmended = useCallback(
    (invoice: Invoice, reason: 'posted' | 'amended') => {
      if (embedded) {
        onInvoiceSaved?.(invoice, reason);
      } else {
        navigate(`/sales-transactions/view/invoice/${invoice.id}`, { replace: true });
      }
    },
    [embedded, navigate, onInvoiceSaved]
  );

  const finishDraftCreated = useCallback(
    (invoice: Invoice) => {
      if (embedded) {
        onInvoiceSaved?.(invoice, 'draft_created');
      } else {
        navigate(`/manage-sales?id=${invoice.id}`, { replace: true });
      }
    },
    [embedded, navigate, onInvoiceSaved]
  );

  const resetAfterPostFailure = useCallback(() => {
    skipNextLoadRef.current = true;
    if (embedded) {
      navigate(
        { pathname: '/sales-transactions/invoice/new', search: location.search },
        { replace: true, preventScrollReset: true }
      );
    } else {
      navigate('/manage-sales', { replace: true });
    }
  }, [embedded, location.search, navigate]);

  const loadStagedAttachments = useCallback(async () => {
    const staged = await listStagedAttachments({
      upload_session_id: uploadSessionId,
      intended_attachable_type: 'invoice',
    }).catch((err) => {
      console.error('[SalesManage] Failed to load staged attachments', err);
      return [];
    });
    setPendingAttachments(staged.map(stagedAttachmentToPending));
  }, [uploadSessionId]);

  const loadAttachments = useCallback(async (invoiceId: number) => {
    const attachments = await getInvoiceAttachments(invoiceId).catch((err) => {
      console.error('[SalesManage] Failed to load attachments', err);
      return [];
    });
    setExistingAttachments(attachments.map(attachmentToEditable));
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setPendingAttachments([]);
      setExistingAttachments([]);
      setEditingInvoice(null);
      customerLookupRef.current.clear();
      setCustomerSearchInput('');
      setCustomerOpenBalance(0);
      setCustomerUnappliedTotal(0);
      setLoadingCustomerAccount(false);

      try {
        const company = await getCompany();
        setAllowNegativeInventory(company.allow_negative_inventory ?? false);
      } catch {
        setAllowNegativeInventory(getAllowNegativeInventory());
      }

      if (isEditMode) {
        const invoice = await getInvoiceById(resolvedEditId);

        if (!canEditInvoice(invoice)) {
          setError(
            'This invoice cannot be edited. Only drafts and open unpaid invoices (no payments applied) can be amended.'
          );
          return;
        }

        setEditingInvoice(invoice);

        const selectedOption: SearchableSelectOption = {
          value: invoice.customer_id,
          label: invoice.customer?.display_name ?? invoice.customer_snapshot.name,
          description:
            [invoice.customer_snapshot.email, invoice.customer_snapshot.phone]
              .filter(Boolean)
              .join(' · ') || undefined,
        };
        setSelectedCustomerOption(selectedOption);
        selectedCustomerOptionRef.current = selectedOption;
        setCustomerOptions([selectedOption]);

        setFormData({
          customer_id: invoice.customer_id,
          customer_snapshot: { ...invoice.customer_snapshot },
          txn_date: invoice.txn_date.slice(0, 10),
          due_date: invoice.due_date.slice(0, 10),
          payment_terms: invoice.payment_terms,
          memo: invoice.memo ?? '',
          notes: invoice.notes ?? '',
          allow_negative_stock: invoice.allow_negative_stock ?? false,
          lines: withTrailingEmptyLine((invoice.lines ?? []).map((line) => mapInvoiceLineToLocal(line))),
        });
        await loadAttachments(invoice.id);
      } else {
        setSelectedCustomerOption(null);
        selectedCustomerOptionRef.current = null;
        setCustomerOptions([]);
        setFormData((prev) => ({
          ...prev,
          lines: createInitialLines(),
        }));
      }

      await loadStagedAttachments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = async (customerId: number) => {
    const id = Number(customerId);
    if (!id) {
      setSelectedCustomerOption(null);
      selectedCustomerOptionRef.current = null;
      setCustomerOpenBalance(0);
      setCustomerUnappliedTotal(0);
      setLoadingCustomerAccount(false);
      setFormData((prev) => ({
        ...prev,
        customer_id: 0,
        customer_snapshot: emptyCustomerSnapshot(),
        payment_terms: 'IMMEDIATE',
        due_date: dueDateFromTerms(prev.txn_date, 'IMMEDIATE'),
      }));
      return;
    }

    let customer = customerLookupRef.current.get(id);
    if (!customer) {
      try {
        customer = await getCustomerById(id);
        customerLookupRef.current.set(id, customer);
      } catch {
        setFormData((prev) => ({ ...prev, customer_id: id }));
        return;
      }
    }

    const option = customerToOption(customer);
    setSelectedCustomerOption(option);
    selectedCustomerOptionRef.current = option;
    setCustomerOptions((prev) =>
      prev.some((opt) => String(opt.value) === String(id)) ? prev : [option, ...prev]
    );

    const terms = customer.payment_terms ?? 'IMMEDIATE';
    setFormData((prev) => ({
      ...prev,
      customer_id: id,
      customer_snapshot: customerToSnapshot(customer),
      payment_terms: terms,
      due_date: dueDateFromTerms(prev.txn_date, terms),
    }));
  };

  const resetBillToFromCustomer = async () => {
    const id = formData.customer_id;
    if (!id) return;

    let customer = customerLookupRef.current.get(id);
    if (!customer) {
      try {
        customer = await getCustomerById(id);
        customerLookupRef.current.set(id, customer);
      } catch {
        toast.error('Could not load customer details.');
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      customer_snapshot: customerToSnapshot(customer),
    }));
  };

  const updateCustomerSnapshot = (patch: Partial<CustomerSnapshot>) => {
    setFormData((prev) => ({
      ...prev,
      customer_snapshot: { ...prev.customer_snapshot, ...patch },
    }));
  };

  const handleTxnDateChange = (txnDate: string) => {
    setFormData((prev) => ({
      ...prev,
      txn_date: txnDate,
      due_date: dueDateFromTerms(txnDate, prev.payment_terms),
    }));
  };

  const handlePaymentTermsChange = (terms: PaymentTerms) => {
    setFormData((prev) => ({
      ...prev,
      payment_terms: terms,
      due_date: dueDateFromTerms(prev.txn_date, terms),
    }));
  };

  const handleCustomerSaved = (customer: Customer) => {
    customerLookupRef.current.set(customer.id, customer);
    setShowAddCustomerModal(false);
    void handleCustomerChange(customer.id);
    toast.success(`Customer "${customer.display_name}" created and selected.`);
  };

  const updateLine = (index: number, patch: Partial<LocalLine>) => {
    setFormData((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    }));
  };

  const handleQtyChange = (index: number, qty: number) => {
    if (!qty || qty < 0.0001) {
      toast.error('Cannot enter 0. Quantity must be greater than zero, or void the invoice.');
      return;
    }
    const line = formData.lines[index];
    updateLine(index, salesQtyChangePricingPatch(line, qty));
  };

  const handleRateChange = (index: number, unit_price: number) => {
    updateLine(index, salesRateChangePricingPatch(unit_price));
  };

  const applyInvoiceDiscount = (value: number, mode: DiscountType) => {
    setFormData((prev) => {
      const active = prev.lines.filter((l) => l.item_id > 0);
      if (active.length === 0) return prev;

      if (mode === 'percent') {
        const pct = Math.min(Math.max(0, value), 100);
        const lines = prev.lines.map((line) =>
          line.item_id === 0
            ? line
            : {
                ...line,
                discount_type: 'percent' as DiscountType,
                discount_percent: pct,
                discount_amount: 0,
              }
        );
        return { ...prev, lines };
      }

      const allocations = allocateAmountDiscountByGross(active, value);
      let activeIdx = 0;
      const lines = prev.lines.map((line) => {
        if (line.item_id === 0) return line;
        const amount = allocations[activeIdx++] ?? 0;
        return {
          ...line,
          discount_type: 'amount' as DiscountType,
          discount_amount: amount,
          discount_percent: 0,
        };
      });
      return { ...prev, lines };
    });
  };

  const handleAmountFocus = (index: number, line: LocalLine) => {
    setLineAmountDrafts((prev) => ({
      ...prev,
      [index]: formatLineAmountDisplay(line, line.item_id > 0),
    }));
  };

  const handleAmountInputChange = (index: number, raw: string) => {
    setLineAmountDrafts((prev) => ({ ...prev, [index]: raw }));
  };

  const handleAmountBlur = (index: number) => {
    const raw = lineAmountDrafts[index];
    setLineAmountDrafts((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    const line = formData.lines[index];
    const committed = salesCommitAmountEntry(raw ?? formatLineAmountDisplay(line, line.item_id > 0), line.qty);
    if (!committed) return;
    updateLine(index, committed);
  };

  const selectItemForLine = (index: number, item: Item | null) => {
    setFormData((prev) => {
      const lines = [...prev.lines];
      const current = lines[index] ?? createEmptyLine();

      if (!item) {
        lines[index] = {
          ...current,
          item_id: 0,
          item_name: '',
          item_sku: '',
          item_type: 'INVENTORY',
          qty_on_hand: '0',
          dealer_selling_price: '0',
          cash_price: '0',
          taxable: true,
        };
        return { ...prev, lines: withTrailingEmptyLine(lines) };
      }

      lines[index] = {
        ...current,
        item_id: item.id,
        item_name: item.name,
        item_sku: item.sku,
        item_type: item.item_type,
        qty_on_hand: item.qty_on_hand,
        dealer_selling_price: item.dealer_selling_price,
        cash_price: item.cash_price,
        taxable: item.taxable,
        qty: current.qty > 0 ? current.qty : 1,
        unit_price: round7(parseFloat(item.unit_price) || 0),
        line_amount: null,
        discount_percent: current.discount_percent,
        description: item.description?.trim() ?? '',
        price_source: 'rate',
      };

      return { ...prev, lines: withTrailingEmptyLine(lines) };
    });

    if (item) {
      const nextIndex = index + 1;
      scrollIntoViewAfterPaint(() => lineRowRefs.current.get(nextIndex));
    }
  };

  const openCreateItemModal = (lineIndex: number) => {
    setCreateItemLineIndex(lineIndex);
    setShowCreateItemModal(true);
  };

  const handleItemCreated = (item: Item, meta?: { imageError?: string }) => {
    if (meta?.imageError) {
      return;
    }

    if (createItemLineIndex !== null) {
      selectItemForLine(createItemLineIndex, item);
    }
    setShowCreateItemModal(false);
    setCreateItemLineIndex(null);
  };

  const removeLine = (index: number) => {
    setFormData((prev) => {
      if (prev.lines.length <= 1) {
        return { ...prev, lines: [createEmptyLine()] };
      }
      const lines = prev.lines.filter((_, i) => i !== index);
      return { ...prev, lines: withTrailingEmptyLine(lines) };
    });
  };

  const buildLinePayload = (lines: LocalLine[] = formData.lines) =>
    lines
      .filter((line) => line.item_id > 0)
      .map((line) => ({
        item_id: line.item_id,
        qty: line.qty,
        unit_price: salesPayloadUnitPrice(line),
        ...(line.discount_type === 'amount'
          ? { discount_amount: line.discount_amount }
          : { discount_percent: line.discount_percent }),
        description: line.description.trim() || null,
      }));

  const buildAttachmentFields = () => {
    const stagedIds = getStagedAttachmentIds(pendingAttachments);
    const includeOnSendById = buildIncludeOnSendById(pendingAttachments);
    return {
      ...(stagedIds.length > 0 ? { staged_attachment_ids: stagedIds } : {}),
      ...(includeOnSendById ? { include_on_send_by_id: includeOnSendById } : {}),
    };
  };

  const buildCreatePayload = (lines: LocalLine[] = formData.lines): CreateInvoiceData => ({
    customer_id: formData.customer_id,
    txn_date: formData.txn_date,
    due_date: formData.due_date,
    payment_terms: formData.payment_terms,
    customer_snapshot: snapshotToPayload(formData.customer_snapshot),
    memo: formData.memo.trim() || null,
    notes: formData.notes.trim() || null,
    ...(formData.allow_negative_stock ? { allow_negative_stock: true } : {}),
    lines: buildLinePayload(lines),
    ...buildAttachmentFields(),
  });

  const buildUpdatePayload = (lines: LocalLine[] = formData.lines): UpdateInvoiceData => ({
    txn_date: formData.txn_date,
    due_date: formData.due_date,
    payment_terms: formData.payment_terms,
    customer_snapshot: snapshotToPayload(formData.customer_snapshot),
    memo: formData.memo.trim() || null,
    notes: formData.notes.trim() || null,
    ...(formData.allow_negative_stock ? { allow_negative_stock: true } : {}),
    lines: buildLinePayload(lines),
    ...buildAttachmentFields(),
  });

  const activeLines = (lines: LocalLine[] = formData.lines) =>
    lines.filter((line) => line.item_id > 0);

  const validateForm = (lines: LocalLine[] = formData.lines) => {
    if (!formData.customer_id) {
      setError('Please select a customer.');
      return false;
    }
    const snapshotError = validateCustomerSnapshot(formData.customer_snapshot);
    if (snapshotError) {
      setError(snapshotError);
      return false;
    }
    if (!formData.txn_date || !formData.due_date) {
      setError('Invoice date and due date are required.');
      return false;
    }
    if (formData.due_date < formData.txn_date) {
      setError('Due date must be on or after the invoice date.');
      return false;
    }
    const active = activeLines(lines);
    if (active.length === 0) {
      setError('Add at least one line item.');
      return false;
    }
    for (const line of active) {
      if (!line.qty || line.qty < 0.0001) {
        setError(`Quantity must be greater than zero for ${line.item_name}.`);
        return false;
      }
      if (line.unit_price < 0) {
        setError(`Unit price cannot be negative for ${line.item_name}.`);
        return false;
      }
      if (line.discount_type === 'percent') {
        if (line.discount_percent < 0 || line.discount_percent > 100) {
          setError(`Discount % must be between 0 and 100 for ${line.item_name}.`);
          return false;
        }
      } else {
        const gross = lineGross(line);
        if (line.discount_amount < 0 || line.discount_amount > gross) {
          setError(`Discount amount cannot exceed the line gross (${formatCurrency(gross)}) for ${line.item_name}.`);
          return false;
        }
      }
      if (line.description.length > 1000) {
        setError(`Description is too long for ${line.item_name} (max 1000 characters).`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async (andPost = false) => {
    blurActiveInputBeforeSave();
    setError(null);
    setFieldErrors({});
    const saveLines = salesApplyAmountDraftsToLines(formData.lines, lineAmountDrafts);
    if (Object.keys(lineAmountDrafts).length > 0) {
      setFormData((prev) => ({ ...prev, lines: saveLines }));
      setLineAmountDrafts({});
    }
    if (!validateForm(saveLines)) return;

    if (andPost && isEditMode && !isDraftEdit) {
      toast.error('Only draft invoices can be posted from this screen.');
      return;
    }

    if (hasPendingUploadInProgress(pendingAttachments)) {
      toast.error('Please wait for file uploads to finish before saving.');
      return;
    }
    if (hasPendingUploadFailed(pendingAttachments)) {
      toast.error('Remove or retry failed file uploads before saving.');
      return;
    }

    const hasExistingAttachmentWork = existingAttachments.some(
      (row) =>
        row.markedForDelete ||
        row.note !== (row.attachment.note ?? '') ||
        row.include_on_send !== row.attachment.include_on_send
    );
    const hasStagedNoteWork = pendingAttachments.some(
      (row) => row.uploadStatus === 'staged' && row.note !== (row.attachment?.note ?? '')
    );

    let savedInvoice: Awaited<ReturnType<typeof createInvoice>> | null = null;
    let attemptedPost = false;

    try {
      setSavePhase('saving');

      if (hasStagedNoteWork) {
        const noteFailures = await syncStagedAttachmentNotes(pendingAttachments);
        if (noteFailures.length > 0) {
          toast.error(`Could not update attachment notes: ${noteFailures.join(' ')}`);
          return;
        }
      }

      savedInvoice = isEditMode
        ? await updateInvoice(resolvedEditId, buildUpdatePayload(saveLines))
        : await createInvoice(buildCreatePayload(saveLines));

      if (hasExistingAttachmentWork && canManageInvoices()) {
        setSavePhase('attachments');
        const { failures } = await processAttachmentChanges({
          parent: 'invoice',
          parentId: savedInvoice.id,
          pending: [],
          existing: existingAttachments,
          showIncludeOnSend: true,
        });

        if (failures.length > 0) {
          toast.error(`Invoice saved. ${failures.length} attachment issue(s): ${failures.join(' ')}`);
          setPendingAttachments([]);
          setUploadSessionId(resetUploadSessionId(INVOICE_UPLOAD_SESSION_KEY));
          await loadAttachments(savedInvoice.id);
          if (!isEditMode) {
            goToEditInvoice(savedInvoice.id);
          }
          return;
        }
      }

      setPendingAttachments([]);
      setUploadSessionId(resetUploadSessionId(INVOICE_UPLOAD_SESSION_KEY));
      await loadAttachments(savedInvoice.id);

      if (andPost) {
        setSavePhase('posting');
        attemptedPost = true;
        await postInvoice(savedInvoice.id);
        toast.success('Invoice posted successfully!');
        finishPostedOrAmended(savedInvoice, 'posted');
      } else if (!isEditMode) {
        toast.success('Draft invoice created.');
        finishDraftCreated(savedInvoice);
      } else if (isEditMode && savedInvoice) {
        if (isOpenAmend) {
          toast.success('Invoice amended. GL and inventory were updated.');
          finishPostedOrAmended(savedInvoice, 'amended');
        } else {
          const updated = savedInvoice;
          setEditingInvoice(updated);
          setFormData((prev) => ({
            ...prev,
            txn_date: updated.txn_date.slice(0, 10),
            due_date: updated.due_date.slice(0, 10),
            payment_terms: updated.payment_terms,
            customer_snapshot: { ...updated.customer_snapshot },
            memo: updated.memo ?? '',
            notes: updated.notes ?? '',
            allow_negative_stock: updated.allow_negative_stock ?? false,
            lines: withTrailingEmptyLine((updated.lines ?? []).map((line) => mapInvoiceLineToLocal(line))),
          }));
          onInvoiceSaved?.(updated, 'draft_updated');
          toast.success('Draft invoice updated.');
        }
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Please try again.';

      if (attemptedPost) {
        if (err instanceof ApiValidationError) {
          setFieldErrors(err.errors);
        }
        toast.error(
          `Posting failed: ${detail} The draft was removed — fix the issue and try again.`
        );
        setExistingAttachments([]);
        if (isEditMode) {
          resetAfterPostFailure();
        }
        return;
      }

      if (!savedInvoice) {
        if (err instanceof ApiValidationError) {
          setFieldErrors(err.errors);
          setError(err.message);
        } else if (isOpenAmend) {
          const amendFallback =
            'Cannot amend this invoice. It may have payments applied, an invalid status, or cleared backorders that require an ADMIN.';
          const message =
            err instanceof Error && err.message.trim() && err.message !== 'Failed to update invoice'
              ? err.message
              : amendFallback;
          toast.error(message);
          setError(message);
        } else {
          toastApiError(err, 'Failed to save invoice');
        }
      } else {
        toast.error(`Invoice saved, but could not complete the next step. ${detail}`);
        if (!isEditMode) {
          goToEditInvoice(savedInvoice.id);
        }
      }
    } finally {
      setSavePhase('idle');
    }
  };

  const savePrimaryLabel = isOpenAmend ? 'Save Changes' : 'Save Draft';

  const saveDraftLabel =
    savePhase === 'saving'
      ? 'Saving...'
      : savePhase === 'attachments'
        ? 'Saving attachments...'
        : savePrimaryLabel;

  const saveAndPostLabel =
    savePhase === 'posting'
      ? 'Posting...'
      : savePhase === 'saving'
        ? 'Saving...'
        : savePhase === 'attachments'
          ? 'Saving attachments...'
          : 'Save & Post';

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const estimatedTotals = useMemo(
    () => estimateInvoiceTotals(formData.lines.filter((line) => line.item_id > 0)),
    [formData.lines]
  );

  const hasLineItems = formData.lines.some((line) => line.item_id > 0);

  const postedLineMeta = useMemo(() => {
    if (!editingInvoice) return new Map<number, InvoiceLine>();
    return new Map((editingInvoice.lines ?? []).map((line) => [line.item_id, line]));
  }, [editingInvoice]);

  const hasPostedBackorders = useMemo(
    () =>
      (editingInvoice?.lines ?? []).some((line) => parseInventoryQty(line.backorder_qty) > 0),
    [editingInvoice]
  );

  if (loading) {
    if (embedded) {
      return (
        <div className="flex flex-1 items-center justify-center min-h-[12rem]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-800" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        embedded ? 'flex flex-col h-full min-h-0 bg-gray-50' : 'flex h-screen bg-gray-50'
      }
    >
      {!embedded && <Sidebar />}

      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className={`flex-1 overflow-auto ${embedded ? 'p-4 pb-28' : 'p-6 pb-28'}`}>
          <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              {!embedded && (
                <Link
                  to="/sales"
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back to Invoices
                </Link>
              )}
              <h1 className="text-2xl font-semibold text-gray-900">
                {isEditMode
                  ? isOpenAmend
                    ? `Amend Invoice ${editingInvoice?.invoice_number ?? ''}`
                    : 'Edit Draft Invoice'
                  : 'New Invoice'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {isOpenAmend
                  ? 'Changes update totals, inventory, and GL immediately.'
                  : 'Create a draft, then post when ready — totals are calculated by the server'}
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400 shrink-0" />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          {isOpenAmend && hasPostedBackorders && (
            <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-lg">
              <p className="text-rose-900 text-sm">
                One or more lines still have open backorder quantity. Amending may change backorder
                qty and estimated COGS.
              </p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-8">
            <section className="pb-8 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900 mb-5">Invoice details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="sm:col-span-2 xl:col-span-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Customer <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <SearchableSelect
                          className="flex-1 min-w-0"
                          value={formData.customer_id}
                          onChange={(id) => void handleCustomerChange(Number(id))}
                          placeholder="Select customer"
                          searchPlaceholder="Search customers..."
                          options={customerOptions}
                          onSearchChange={setCustomerSearchInput}
                          isLoading={customerSearchLoading}
                          disabled={isEditMode}
                        />
                        {!isEditMode && (
                          <button
                            type="button"
                            onClick={() => setShowAddCustomerModal(true)}
                            className="flex-shrink-0 inline-flex items-center px-3 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                            title="Create new customer"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {isEditMode && (
                        <p className="mt-1 text-xs text-gray-500">
                          Customer cannot be changed after the invoice is created.
                        </p>
                      )}
                      {fieldError('customer_id') && (
                        <p className="mt-1 text-sm text-red-600">{fieldError('customer_id')}</p>
                      )}
                    </div>

                    {formData.customer_id > 0 && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <label className="block text-sm font-medium text-gray-700">
                              Bill to <span className="font-normal text-gray-500">(printed on invoice)</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => void resetBillToFromCustomer()}
                              className="text-xs font-medium text-slate-600 hover:text-slate-900 shrink-0"
                            >
                              Reset to customer
                            </button>
                          </div>
                          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 space-y-2">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <div className="min-w-0">
                                <input
                                  type="text"
                                  maxLength={255}
                                  value={formData.customer_snapshot.name}
                                  onChange={(e) => updateCustomerSnapshot({ name: e.target.value })}
                                  placeholder="Name *"
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white"
                                />
                                {fieldError('customer_snapshot.name') && (
                                  <p className="mt-0.5 text-xs text-red-600">{fieldError('customer_snapshot.name')}</p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <input
                                  type="email"
                                  value={formData.customer_snapshot.email ?? ''}
                                  onChange={(e) =>
                                    updateCustomerSnapshot({ email: e.target.value || null })
                                  }
                                  placeholder="Email"
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white"
                                />
                                {fieldError('customer_snapshot.email') && (
                                  <p className="mt-0.5 text-xs text-red-600">{fieldError('customer_snapshot.email')}</p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <input
                                  type="text"
                                  maxLength={50}
                                  value={formData.customer_snapshot.phone ?? ''}
                                  onChange={(e) =>
                                    updateCustomerSnapshot({ phone: e.target.value || null })
                                  }
                                  placeholder="Phone"
                                  className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white"
                                />
                                {fieldError('customer_snapshot.phone') && (
                                  <p className="mt-0.5 text-xs text-red-600">{fieldError('customer_snapshot.phone')}</p>
                                )}
                              </div>
                            </div>
                            <div>
                              <textarea
                                rows={2}
                                maxLength={2000}
                                value={formData.customer_snapshot.address ?? ''}
                                onChange={(e) =>
                                  updateCustomerSnapshot({ address: e.target.value || null })
                                }
                                placeholder="Address"
                                className="w-full border border-gray-300 rounded-lg px-2.5 py-2 text-sm bg-white resize-y min-h-[2.5rem]"
                              />
                              {fieldError('customer_snapshot.address') && (
                                <p className="mt-0.5 text-xs text-red-600">{fieldError('customer_snapshot.address')}</p>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              Invoice only — does not update the customer record.
                            </p>
                          </div>
                        </div>

                        <div className="min-w-0">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Customer account
                          </label>
                          <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-3 py-2.5 space-y-1.5 text-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-500">Open balance</span>
                              <span className="font-semibold text-red-600 tabular-nums whitespace-nowrap">
                                {loadingCustomerAccount ? '—' : formatCurrency(customerOpenBalance)}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-gray-500">Unapplied payment</span>
                              <span className="font-semibold text-amber-700 tabular-nums whitespace-nowrap">
                                {loadingCustomerAccount ? '—' : formatCurrency(customerUnappliedTotal)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Invoice date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.txn_date}
                    onChange={(e) => handleTxnDateChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Due date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    min={formData.txn_date}
                    onChange={(e) => setFormData((prev) => ({ ...prev, due_date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-400">Auto-set from payment terms</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment terms</label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => handlePaymentTermsChange(e.target.value as PaymentTerms)}
                    className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white"
                  >
                    {PAYMENT_TERMS.map((term) => (
                      <option key={term} value={term}>
                        {formatLabel(term)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="pb-8 border-b border-gray-100">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <h3 className="text-base font-semibold text-gray-900">Line items</h3>
                {showNegativeStockOption && (
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.allow_negative_stock}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          allow_negative_stock: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300 text-slate-800 focus:ring-slate-800"
                    />
                    Allow negative stock
                  </label>
                )}
              </div>
              {fieldError('allow_negative_stock') && (
                <p className="mb-3 text-sm text-red-600">{fieldError('allow_negative_stock')}</p>
              )}

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 text-sm table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-5 px-0.5 py-2.5 text-center text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="w-[18%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Product / service
                      </th>
                      <th className="w-[28%] px-2 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="w-[9%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                      <th className="w-[11%] px-2 py-2.5 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="w-8 px-1 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {formData.lines.map((line, index) => {
                      const isEmptyRow = line.item_id === 0;
                      const postedLine = postedLineMeta.get(line.item_id);
                      const exceedsOnHand =
                        line.item_type === 'INVENTORY' &&
                        line.item_id > 0 &&
                        lineExceedsOnHand(line.qty, line.qty_on_hand);
                      const showOversellWarning =
                        exceedsOnHand &&
                        (formData.allow_negative_stock || isOpenAmend);
                      return (
                      <tr
                        key={`line-${index}`}
                        ref={(el) => {
                          if (el) lineRowRefs.current.set(index, el);
                          else lineRowRefs.current.delete(index);
                        }}
                        className={`align-top ${isEmptyRow ? 'bg-white' : 'bg-slate-50/40'}`}
                      >
                        <td className="w-5 px-0.5 py-2 text-center text-gray-500 tabular-nums">{index + 1}</td>
                        <td className="px-2 py-2">
                          <div className="flex gap-1">
                            <AsyncItemSelect
                              className="flex-1 min-w-0"
                              value={line.item_id}
                              selectedLabel={line.item_name}
                              onSelect={(item) => selectItemForLine(index, item)}
                              onClear={() => selectItemForLine(index, null)}
                              placeholder="Select item"
                              searchPlaceholder="Search by name or SKU..."
                            />
                            <button
                              type="button"
                              onClick={() => openCreateItemModal(index)}
                              className="flex-shrink-0 inline-flex items-center px-2 py-2.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100"
                              title="Create new item"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          {line.item_id > 0 && (
                            <p className="mt-1 text-xs text-gray-400 font-mono truncate">{line.item_sku}</p>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <textarea
                            ref={(el) => { if (el) autoResize(el); }}
                            rows={1}
                            maxLength={1000}
                            value={line.description}
                            onChange={(e) => {
                              updateLine(index, { description: e.target.value });
                              autoResize(e.target);
                            }}
                            placeholder="Description"
                            className={`${lineInputClass} min-h-[2.25rem] resize-none overflow-hidden`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <DecimalInput
                            value={line.qty}
                            onCommit={(qty) => handleQtyChange(index, qty)}
                            disabled={!line.item_id}
                            className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                          />
                          {line.item_id > 0 && (
                            <div className="mt-1 space-y-0.5 text-xs text-right">
                              {line.item_type === 'INVENTORY' && (
                                <p className={`truncate ${qtyOnHandClassName(line.qty_on_hand)}`}>
                                  On hand: {formatPreviewQuantity(line.qty_on_hand)}
                                </p>
                              )}
                              {showOversellWarning && (
                                <p className="truncate text-amber-700 font-medium">
                                  Exceeds on hand
                                  {!formData.allow_negative_stock && !isOpenAmend
                                    ? ' — enable oversell or reduce qty'
                                    : ''}
                                </p>
                              )}
                              {postedLine && parseInventoryQty(postedLine.backorder_qty) > 0 && (
                                <>
                                  <p className="truncate text-rose-700">
                                    Backorder: {postedLine.backorder_qty}
                                  </p>
                                  <p className="truncate text-gray-500">
                                    Est. COGS: {formatCurrency(postedLine.estimated_cogs_amount ?? '0')}
                                  </p>
                                  <p className="truncate text-gray-500">
                                    COGS: {formatCurrency(postedLine.cogs_amount)}
                                  </p>
                                </>
                              )}
                              <p className="truncate">Dealer: {formatCurrency(line.dealer_selling_price)}</p>
                              <p className="truncate">Cash: {formatCurrency(line.cash_price)}</p>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <DecimalInput
                            value={line.unit_price}
                            onCommit={(unit_price) => handleRateChange(index, unit_price)}
                            min={0}
                            decimals={4}
                            formatDisplay={(n) => String(salesDisplayRate(n))}
                            disabled={!line.item_id}
                            className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                          />
                        </td>
                        <td className="px-2 py-2 text-right text-sm tabular-nums">
                          {line.item_id > 0 ? (
                            line.discount_type === 'percent' ? (
                              <span className={line.discount_percent > 0 ? 'text-red-600' : 'text-gray-400'}>
                                {line.discount_percent > 0 ? `${line.discount_percent}%` : '—'}
                              </span>
                            ) : (
                              <span className={line.discount_amount > 0 ? 'text-red-600' : 'text-gray-400'}>
                                {line.discount_amount > 0 ? formatCurrency(line.discount_amount) : '—'}
                              </span>
                            )
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min={0}
                            step="any"
                            value={
                              lineAmountDrafts[index] ??
                              formatLineAmountDisplay(line, line.item_id > 0)
                            }
                            onFocus={(e) => {
                              handleAmountFocus(index, line);
                              selectInputOnFocus(e);
                            }}
                            onChange={(e) => handleAmountInputChange(index, e.target.value)}
                            onBlur={() => handleAmountBlur(index)}
                            disabled={!line.item_id}
                            className={`${lineInputClass} text-right disabled:bg-gray-50 disabled:text-gray-400`}
                          />
                        </td>
                        <td className="px-1 py-2">
                          <button
                            type="button"
                            onClick={() => removeLine(index)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                            title="Remove line"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Memo</label>
                      <input
                        type="text"
                        value={formData.memo}
                        onChange={(e) => setFormData((prev) => ({ ...prev, memo: e.target.value }))}
                        placeholder="Printed on invoice"
                        className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Internal Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        placeholder="Internal notes only"
                        className="w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm"
                      />
                    </div>
                  </div>

                  <AttachmentsSection
                    title="Attachments"
                    description="Signed POs, delivery notes, photos, and other documents. Files upload immediately and link when you save."
                    showIncludeOnSend
                    parentIsDraft
                    stageOnPick
                    intendedAttachableType="invoice"
                    uploadSessionId={uploadSessionId}
                    pending={pendingAttachments}
                    onPendingChange={setPendingAttachments}
                    existing={existingAttachments}
                    onExistingChange={setExistingAttachments}
                    disabled={submitting}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Estimated breakdown
                  </p>
                  {hasLineItems ? (
                    <div className="space-y-2.5 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-gray-600 whitespace-nowrap">Discount</span>
                        <div className="flex items-center gap-2">
                          {estimatedTotals.discountTotal > 0 && invoiceDiscountDraft === null && (
                            <span className="text-xs text-red-500 font-medium">
                              −{formatCurrency(estimatedTotals.discountTotal)}
                            </span>
                          )}
                          <div className="flex flex-col items-end gap-1">
                            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs font-medium">
                              <button
                                type="button"
                                onClick={() => { setInvoiceDiscountType('percent'); setInvoiceDiscountDraft(null); }}
                                className={`px-2.5 py-1 transition-colors ${
                                  invoiceDiscountType === 'percent'
                                    ? 'bg-slate-700 text-white'
                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                % Percent
                              </button>
                              <button
                                type="button"
                                onClick={() => { setInvoiceDiscountType('amount'); setInvoiceDiscountDraft(null); }}
                                className={`px-2.5 py-1 border-l border-gray-300 transition-colors ${
                                  invoiceDiscountType === 'amount'
                                    ? 'bg-slate-700 text-white'
                                    : 'bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                $ Amount
                              </button>
                            </div>
                            <input
                              type="number"
                              min={0}
                              max={invoiceDiscountType === 'percent' ? 100 : undefined}
                              step="any"
                              placeholder="0.00"
                              value={invoiceDiscountDraft ?? ''}
                              onFocus={() => {
                                if (invoiceDiscountType === 'amount' && estimatedTotals.discountTotal > 0) {
                                  setInvoiceDiscountDraft(String(estimatedTotals.discountTotal));
                                } else {
                                  setInvoiceDiscountDraft('');
                                }
                              }}
                              onChange={(e) => setInvoiceDiscountDraft(e.target.value)}
                              onBlur={() => {
                                const val = parseFloat(invoiceDiscountDraft ?? '');
                                setInvoiceDiscountDraft(null);
                                if (!isNaN(val) && val >= 0) applyInvoiceDiscount(val, invoiceDiscountType);
                              }}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-right text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(estimatedTotals.subtotal)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax (est. {ESTIMATED_TAX_RATE}%)</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(estimatedTotals.taxTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between pt-3 mt-1 border-t border-slate-200">
                        <span className="text-base font-semibold text-gray-900">Total</span>
                        <span className="text-base font-bold text-slate-800">
                          {formatCurrency(estimatedTotals.total)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Add line items to see totals</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-gray-600">
              {hasLineItems ? (
                <>
                  <span className="text-gray-500 mr-3">
                    Subtotal {formatCurrency(estimatedTotals.subtotal)}
                  </span>
                  <span className="text-gray-500 mr-3">
                    Tax {formatCurrency(estimatedTotals.taxTotal)}
                  </span>
                  <span className="text-lg font-bold text-slate-800">
                    {formatCurrency(estimatedTotals.total)}
                  </span>
                </>
              ) : (
                <span className="text-gray-400">No line items</span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => (embedded ? onClose?.() : navigate('/sales'))}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSave(false)}
              disabled={submitting || !hasLineItems}
              className={`${btnPrimary} px-6 py-2.5 text-sm font-semibold disabled:opacity-50`}
            >
              {saveDraftLabel}
            </button>
            {(!isEditMode || isDraftEdit) && (
              <button
                type="button"
                onClick={() => handleSave(true)}
                disabled={submitting || !hasLineItems}
                className="px-6 py-2.5 text-sm font-semibold rounded-lg text-white bg-green-700 hover:bg-green-800 disabled:opacity-50"
              >
                {saveAndPostLabel}
              </button>
            )}
            </div>
          </div>
        </div>
      </div>

      {showAddCustomerModal && (
        <CustomerFormModal
          subtitle="Adds the customer to your list and selects them on this invoice."
          onClose={() => setShowAddCustomerModal(false)}
          onSaved={handleCustomerSaved}
        />
      )}

      {showCreateItemModal && (
        <ItemFormModal
          subtitle="Adds the item to your catalog and selects it on this line."
          onClose={() => {
            setShowCreateItemModal(false);
            setCreateItemLineIndex(null);
          }}
          onSaved={handleItemCreated}
        />
      )}
    </div>
  );
};

export default SalesManage;
